import { storage } from '../../storage';
import { DomainHandler, HandlerContext } from '../../types';
import {
  ANALYSIS_TERMINAL_STATUSES,
  BACKEND_STATUS_TO_PROGRESS,
  AnalysisProgressData,
  AnalysisProgressStatus,
  AnalysisWsMessage,
  AnalysisWsMessageType,
} from './analysis.types';

const MESSAGE_STATUS_MAP: Record<AnalysisWsMessageType, AnalysisProgressStatus> = {
  analysis_waiting: 'WAITING',
  analysis_started: 'PROCESSING',
  analysis_progress_update: 'PROCESSING',
  // FINISHED keeps socket open so persistence_completed can still arrive
  analysis_finished: 'FINISHED',
  analysis_error: 'ERROR',
  analysis_canceled: 'CANCELED',
  // COMPLETED is the true terminal — set by persistence_completed with is_fully_completed
  analysis_persistence_completed: 'COMPLETED',
};

const VALID_TYPES = new Set<string>(Object.keys(MESSAGE_STATUS_MAP));

function isValidMessage(msg: unknown): msg is AnalysisWsMessage {
  if (typeof msg !== 'object' || msg === null) return false;
  const m = msg as any;
  return (
    typeof m.type === 'string' &&
    VALID_TYPES.has(m.type) &&
    typeof m.data === 'object' &&
    m.data !== null &&
    typeof m.data.id === 'number'
  );
}

export const analysisHandler: DomainHandler = {
  onMessage(msg: unknown, ctx: HandlerContext): boolean {
    if (!isValidMessage(msg)) return false;

    const { type, data } = msg;
    const { storageKey } = ctx.descriptor;

    const prev = storage.get<AnalysisProgressData>(storageKey) ?? ({ id: data.id } as AnalysisProgressData);

    const status: AnalysisProgressStatus | undefined =
      (data.status !== undefined ? BACKEND_STATUS_TO_PROGRESS[data.status] : undefined) ??
      MESSAGE_STATUS_MAP[type];

    if (!status) return false;

    // A replayed snapshot can land after a newer tick: never go backwards while processing.
    const keepsProcessing = status === 'PROCESSING' && prev.status === 'PROCESSING';
    const progress = keepsProcessing
      ? Math.max(prev.progress ?? 0, data.progress ?? 0)
      : data.progress ?? prev.progress;

    const next: AnalysisProgressData = {
      ...prev,
      id: data.id,
      status,
      progress,
      message: data.message ?? prev.message,
      remaining: data.remaining ?? prev.remaining,
      elapsed: data.elapsed ?? prev.elapsed,
      ended_at: data.ended_at ?? prev.ended_at,
      error_message: data.error_message ?? prev.error_message,
      ...(data.is_fully_completed !== undefined ? { is_fully_completed: data.is_fully_completed } : {}),
    };

    storage.set(storageKey, next);

    return ANALYSIS_TERMINAL_STATUSES.includes(status);
  },
};
